import React from 'react';
import {DateRangePicker, DateRangePickerProps} from 'rsuite';
import {Control, Controller, RegisterOptions, useController} from 'react-hook-form';
import {t} from '@lingui/core/macro';
import cn from 'classnames';

import Correct from 'shared/ui/icons/General/Correct';
import Wrong from 'shared/ui/icons/General/Wrong';
import Edit from 'shared/ui/icons/General/Edit';
import {getInputState} from 'shared/ui/Input/inputHelper';
import {isTranslate} from 'shared/lib/lang';

import {PALETTE} from 'shared/config/model/palette';
import {brandColor} from 'shared/lib/colors';

import styles from './DateRangePickerControlled.module.scss';

export interface IDateRangePickerControlledProps extends DateRangePickerProps{
    control: Control<any>,
    name: string,
    rules?: RegisterOptions,
    validate?: boolean;
    minDate?: Date;
    // eslint-disable-next-line no-unused-vars
    shouldDisableDate?: (date: Date) => boolean;
}

const DateRangePickerControlled: React.FC<IDateRangePickerControlledProps> = ({
    control,
    name,
    rules,
    validate = true,
    defaultValue,
    onChange,
    placeholder= t`Выберите диапазон дат...`,
    minDate,
    ...rest
}) => {
    const { field, fieldState } = useController({
        name,
        control,
        defaultValue,
    });

    const dateFormat = !isTranslate() ? 'dd.MM.yyyy' : 'yyyy-MM-dd';

    const state = getInputState({
        fieldState,
        value: field.value,
        validate,
    });

    const iconsMap = {
        valid: <Correct color={brandColor(PALETTE.accent, PALETTE.success)}/>,
        error: <Wrong/>,
        filled: <Edit />,
        default: <Edit />,
    };

    const classMap = isTranslate()
        ? {
            valid: styles.enItem_valid,
            error: styles.enItem_error,
            filled: styles.enItem_filled,
            default: styles.enItem_default,
        }
        : {
            valid: styles.item_valid,
            error: styles.item_error,
            filled: styles.item_filled,
            default: styles.item_default,
        };

    const baseClass = isTranslate() ? styles.enItem : styles.item;

    const shouldDisableDate = (date: Date) => {
        if (minDate) {
            return date < minDate;
        }
        return false;
    };

    return (
        <div className={styles.item_container}>
            <div className={styles.icon}>
                {iconsMap[state]}
            </div>
            <Controller
                control={control}
                name={name}
                rules={rules}
                defaultValue={defaultValue}
                /* eslint-disable-next-line no-unused-vars */
                render={({field: {ref, onChange: onChangeFrom, ...field}}) => (
                    <DateRangePicker
                        onChange={(value, event) => {
                            onChangeFrom(value);
                            onChange && onChange(value, event);
                        }}
                        className={cn(
                            baseClass,
                            classMap[state],
                        )}
                        placeholder={placeholder}
                        menuClassName={isTranslate() ? 'english-calendar-menu' : ''}
                        format={dateFormat}
                        showOneCalendar
                        preventOverflow={false}
                        shouldDisableDate={shouldDisableDate}
                        isoWeek
                        {...rest}
                        {...field}
                    />
                )}
            />
        </div>
    );
};

export default DateRangePickerControlled;